import './style.modules.scss'

import {
  Content,
  Link,
  LinkVariant,
  Spacer,
  SpacerSpacing
} from '@ampcore/adel-react'
import { BackgroundColor } from '@ampcore/ampcom-contracts'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useMemo } from 'react'
import xss from 'xss'

import { Link as LinkType } from '../../types/Link.type'
import { FacetImage } from '../FacetImage/FacetImage'
import { HeroTopicFrontBackgroundColor } from './HeroTopicFrontColor.type'
import { HeroTopicFrontVariant } from './HeroTopicFrontVariant.type'

export type HeroTopicFrontWithTextProps = {
  variant: HeroTopicFrontVariant.TEXT
  heading?: string
  color?: HeroTopicFrontBackgroundColor
  content?: string
  button?: LinkType
  analyticsName?: string
  componentSpacing?: SpacerSpacing
  testId?: string
}

export const HeroTopicFrontWithText = (props: HeroTopicFrontWithTextProps) => {
  const {
    heading,
    content,
    color,
    button,
    analyticsName,
    componentSpacing,
    testId
  } = props

  const atb = useMemo(() => {
    return new AnalyticsTagBuilder()
      .setDefaultName('HeroTopicFront')
      .setAnalyticsName(analyticsName)
  }, [analyticsName])

  const isReversedContent = color !== BackgroundColor.WHITE
  const facetColor = color || BackgroundColor.MIDNIGHT_BLUE
  const largeFacet = `${window.AMPCOMPATH}images/facets/heroes/topic-front/desktop/${facetColor}.svg`
  const mobileFacet = `${window.AMPCOMPATH}images/facets/heroes/topic-front/mobile/${facetColor}.svg`

  return (
    <Spacer spacing={componentSpacing}>
      <section
        className="HeroTopicFront--withText u-posRelative u-marginTop48"
        data-testid={testId ? `${testId}-text` : undefined}
      >
        <div className="HeroTopicFront-textContainer u-sizeConstrained">
          <div className="HeroTopicFront-textInner u-posRelative u-flex u-flexCol u-flexJustifyCenter u-flexAlignItemsStart">
            <FacetImage
              color={facetColor}
              src={largeFacet}
              sm={mobileFacet}
              priority={true}
              className="HeroTopicFront-bgImage u-posAbsolute"
              testId={testId ? `${testId}-facet` : undefined}
            />
            <div className="HeroTopicFront-textContent u-paddingHoriz64 u-paddingVert64 u-md-paddingHoriz48 u-md-paddingVert48 u-sm-paddingHoriz40 u-sm-paddingVert40">
              {heading && (
                <h1
                  className={['Type-1', isReversedContent && 'u-textColorWhite']
                    .filter(Boolean)
                    .join(' ')}
                  data-testid={testId ? `${testId}-heading` : null}
                  dangerouslySetInnerHTML={{ __html: xss(heading) }}
                ></h1>
              )}
              {content && (
                <Content
                  large
                  reversed={isReversedContent}
                  className="u-marginTop8"
                  testId={testId ? `${testId}-content` : undefined}
                  dangerouslySetInnerHTML={{ __html: xss(content) }}
                />
              )}
              {button?.name && button?.url && (
                <Link
                  className={`u-marginTop24 ${
                    isReversedContent ? 'Button--reversed' : ''
                  }`}
                  variant={
                    isReversedContent
                      ? LinkVariant.SECONDARY
                      : LinkVariant.PRIMARY
                  }
                  name={button.name}
                  url={button.url}
                  target={button.target}
                  testId={testId ? `${testId}-link` : undefined}
                  {...atb.dataAnalyticAttribute('button')}
                />
              )}
            </div>
          </div>
        </div>
      </section>
    </Spacer>
  )
}
